// The reader's blunders or misses of the last 30 days, each classified
// (`classify.ts` of `@chess/api-mistakes`) with its motif (the
// `mistakes.motifs` service, memoised per fact so a mistake is classified
// once however often the store publishes), what it cost in the decisive
// zone (`costOf`), how the reader stood before it and their clock: the rows
// of the Blunders or Misses screen's table, which its chips filter and its
// toggle sorts (`table.ts`). One aggregator per kind; the store runs it once.
import { aggregatorId, type Accumulator, type Aggregator, type AggregatorId, type GameFacts, type MistakeStanding, type MoveKind } from '@chess/api-model';
import { byRecency, classify, clockBucketOf, costOf, motifLabel, standingOf, type ClassifiedMistake, type ClockBucket, type Motif, type MotifOf } from '@chess/api-mistakes';

/** One row of the table: the classified mistake, its motif, and the groups the chips filter by. */
export interface MistakeRow extends ClassifiedMistake {
  /** The motif (family and variation), the table's Pattern column and the Pattern and variation chips. */
  motif: Motif;
  /** Pawns given up (a blunder) or passed over (a miss) while the game was within the decisive zone (`costOf`). */
  cost: number;
  /** How the reader stood before the move, by the Winning setting; absent in facts read before it was kept. */
  standing?: MistakeStanding;
  /** The reader's clock before the move; absent without clock times. */
  clock?: ClockBucket;
}

export interface MistakesResult {
  kind: MoveKind;
  /** Newest game first, then in move order. */
  mistakes: MistakeRow[];
  /** Scanned games of the period: what the counts are out of. */
  scanned: number;
}

class MistakesAccumulator implements Accumulator<MistakesResult> {
  private readonly kind: MoveKind;
  private readonly motifOf: MotifOf;
  private readonly mistakes: MistakeRow[] = [];
  private scanned = 0;

  constructor(kind: MoveKind, motifOf: MotifOf) {
    this.kind = kind;
    this.motifOf = motifOf;
  }

  add(game: GameFacts): void {
    if (!game.scanned) return;
    this.scanned += 1;
    for (const m of game.mistakes ?? []) {
      if (m.kind !== this.kind) continue;
      const classified = classify(m, game);
      const motif = this.motifOf(m, game.colour);
      // The context of a middlegame mistake is its motif, as the Pattern column names it.
      const context = m.stage === 'middlegame' ? motifLabel(motif) : classified.context;
      this.mistakes.push({ ...classified, context, motif, cost: costOf(m), standing: standingOf(m), clock: clockBucketOf(m) });
    }
  }

  result(): MistakesResult {
    return { kind: this.kind, mistakes: [...this.mistakes].sort(byRecency), scanned: this.scanned };
  }
}

export class MistakesAggregator implements Aggregator<MistakesResult> {
  readonly id: AggregatorId;
  readonly kind: MoveKind;
  private readonly motifOf: MotifOf;

  /** `motifOf`: the motif classifier, memoised (`memoisedMotifs` over the `mistakes.motifs` service). */
  constructor(kind: MoveKind, motifOf: MotifOf) {
    this.kind = kind;
    this.motifOf = motifOf;
    this.id = aggregatorId(`mistakes-${kind}`);
  }

  start(): Accumulator<MistakesResult> {
    return new MistakesAccumulator(this.kind, this.motifOf);
  }
}
